<template>
  <div class="categories-list">
    <BaseCheckbox
      :hollow="true"
      :guid="`${category.guid}-all`"
      :label="$t('message.filters.labels.all')"
      :value="'all'"
      :active="allModel"
      v-if="category.all && query === ''"
      @change="handleAllchange"
    />

    <ul class="categories-list__list" v-if="items.length">
      <li
        v-for="item in items"
        :key="item.guid"
        class="categories-list__item"
        :style="itemIndent(item)">

        <BaseCheckbox
          :hollow="true"
          :guid="item.guid"
          :label="item.label"
          :value="item.value"
          :active="item.active"
          :name="item.name || null"
          :colorHex="item.color || ''"
          v-show="item.show"
          @change="handleChange"
        />
      </li>
    </ul>
  </div>
</template>

<script>
import BaseCheckbox from '@base/BaseCheckbox'

export default {
  name: 'TheCategoriesList',
  props: {
    category: {
      type: Object,
      required: true
    },
    categoryIndex: {
      type: Number,
      required: true
    },
    query: {
      type: String,
      required: true
    },
    context: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      allLength: 0,
      allModel: false,
      filteredItems: {}
    }
  },
  computed: {
    selected () {
      return this.$store.getters[`${this.context}/getSelected`]
    },
    items () {
      return this.category.items
    },
    // Depth of every item, walking up the `parent` guids, so each level of the
    // tree can be indented one step further than the one above it.
    depths () {
      const byGuid = {}
      this.items.forEach(item => { byGuid[item.guid] = item })

      const depths = {}
      this.items.forEach(item => {
        let depth = 0
        let current = item
        while (current.parent && byGuid[current.parent] && depth < 10) {
          current = byGuid[current.parent]
          depth += 1
        }
        depths[item.guid] = depth
      })

      return depths
    },
    calculatedLength () {
      let length = 0
      this.items.forEach(item => {
        if (item.show) length += 1
      })
      return length
    }
  },
  watch: {
    items: {
      handler (val, oldVal) {
        this.$store.dispatch(`${this.context}/updateSelected`)
        this.allModel = this.selected[this.categoryIndex].items.length === this.calculatedLength && this.calculatedLength !== 0
      },
      deep: true
    },
    selected () {
      this.$emit('update', this.selected[this.categoryIndex].items.length)
    },
    query () {
      this.$store.dispatch(`${this.context}/updateAllItemsShow`, { categoryIndex: this.categoryIndex, query: this.query })
    }
  },
  components: {
    BaseCheckbox
  },
  mounted () {
    // The list can be mounted after the view already applied the filters of the
    // url, in which case this is a no-op ; it keeps the checkboxes in sync when
    // the categories are rendered later on.
    const urlFilters = this.context === 'filters' ? this.$route.query.filters : ''
    if (urlFilters) this.$store.dispatch(`${this.context}/applyUrlFilters`, urlFilters)
    else this.$store.dispatch(`${this.context}/updateSelected`)
  },
  methods: {
    // One step of padding per level of nesting, relative to the parent.
    itemIndent (item) {
      const depth = this.depths[item.guid] || 0
      return depth ? { paddingLeft: `${depth * 2}rem` } : null
    },
    handleAllchange ({ active }) {
      const condition = (active || (!active && (this.calculatedLength === this.selected[this.categoryIndex].items.length || !this.selected[this.categoryIndex].items.length)))
      if (condition) {
        this.$store.dispatch(`${this.context}/updateAllItemsActive`, {
          categoryIndex: this.categoryIndex,
          active
        })
      }
    },
    handleChange ({ guid, active }) {
      this.$store.dispatch(`${this.context}/updateItemActive`, {
        categoryIndex: this.categoryIndex,
        guid,
        active
      })
    }
  }
}
</script>

<style lang="scss">
  .categories-list{
    padding-top: layout("spacing");
    max-height: 26rem;
    overflow-y: auto;

    > .checkbox{
      margin-bottom: layout("spacing");
    }

    // List

    &__list{
      @include no-type-list;
    }

    // Checkbox
    .checkbox{
      color: colors("font");

      label{
        font-weight: font-weights("normal");
      }

      &__square{
        border: 1px solid tint(colors("font"), 50%);
        background-color: white;

        &::before{
          color: colors("primary");
        }

        .filters &{
          border: 1px solid white;
          background-color: transparent;

          &::before{
            color: white;
          }
        }
      }

      .filters &{
        color: white;

        label{
          font-weight: font-weights("bold");
        }
      }
    }
  }
</style>
